import {Component, forwardRef, inject, OnDestroy, OnInit, input} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {TranslocoPipe} from '@jsverse/transloco';
import {Message} from 'primeng/message';
import {CountrySelectComponent} from '../../inputs/country-select/country-select.component';
import {FormFieldComponent} from '../../inputs/form-field/form-field.component';
import {Subscription} from 'rxjs';
import {InvoiceDataRequest} from '@frontend/shared';
import {FormValidationService} from '../../../services/form-validation.service';

export interface BillingFormValue {
  legalCompanyName: string;
  legalForm: string;
  country: string;
  zipCode: string;
  city: string;
  additionalInformation?: string;
  street: string;
  number: string;
  vatNumber: string;
  invoiceEmail: string;
  responsibleForInvoices: string;
  costCenter: string;
  internalInformation: string;
}

@Component({
  selector: 'app-billing-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoPipe,
    Message,
    CountrySelectComponent,
    FormFieldComponent,
    FormsModule,
  ],
  templateUrl: './billing-form.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BillingFormComponent),
      multi: true,
    },
  ],
})
export class BillingFormComponent implements OnInit, OnDestroy, ControlValueAccessor {
  readonly initiallyDisabled = input(false);

  private readonly fb = inject(FormBuilder);
  private readonly formValidation = inject(FormValidationService);

  form!: FormGroup;
  disabled = false;

  private onChange!: (value: BillingFormValue | null) => void;
  private onTouched!: () => void;
  private formSubscription?: Subscription;

  countryInvalid(): boolean {
    const control = this.form?.get('country');
    return control ? control.invalid && control.touched : false;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormValueChanges();

    if (this.initiallyDisabled()) {
      this.form.disable();
    }
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      legalCompanyName: ['', Validators.required],
      legalForm: ['', Validators.required],
      country: ['', Validators.required],
      zipCode: ['', Validators.required],
      city: ['', Validators.required],
      additionalInformation: [''],
      street: ['', Validators.required],
      number: ['', Validators.required],
      vatNumber: ['', Validators.required],
      invoiceEmail: ['', [Validators.required, this.formValidation.emailValidator()]],
      responsibleForInvoices: ['', [Validators.required, this.formValidation.emailValidator()]],
      costCenter: [''],
      internalInformation: [''],
    });
  }

  private setupFormValueChanges(): void {
    this.formSubscription = this.form.valueChanges.subscribe((value) => {
      if (this.onChange) {
        this.onChange(value);
      }
    });
  }

  buildInvoiceDataRequest(): InvoiceDataRequest {
    const formValues = this.form.value;
    return {
      legalCompanyName: formValues.legalCompanyName as string,
      legalForm: formValues.legalForm as string,
      country: formValues.country as string,
      zipCode: formValues.zipCode as string,
      city: formValues.city as string,
      additionalInformation: formValues.additionalInformation ?? undefined,
      street: formValues.street as string,
      number: formValues.number as string,
      vatNumber: formValues.vatNumber as string,
      invoiceEmail: formValues.invoiceEmail as string,
      responsibleForInvoicesEmail: formValues.responsibleForInvoices as string,
      costCenter: formValues.costCenter as string,
      internalInformation: formValues.internalInformation as string,
    };
  }

  toggleFormControlsState(enabled: boolean): void {
    const controls = [
      'legalCompanyName',
      'legalForm',
      'country',
      'zipCode',
      'city',
      'additionalInformation',
      'street',
      'number',
      'vatNumber',
      'invoiceEmail',
      'responsibleForInvoices',
      'costCenter',
      'internalInformation',
    ];

    for (const controlName of controls) {
      const control = this.form.get(controlName);
      if (control) {
        if (enabled) {
          control.enable();
        } else {
          control.disable();
        }
      }
    }
  }

  writeValue(value: BillingFormValue | null): void {
    if (value) {
      this.form.patchValue(value, {emitEvent: false});
    } else {
      this.form.reset({}, {emitEvent: false});
    }
  }

  registerOnChange(fn: (value: BillingFormValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
